// Lookups over a code map for the inspector, the explorer and the palette: members and types by id,
// the file each lives in, and calls in both directions.
import { TOP_LEVEL_MAIN, type CodeCall, type CodeMap, type CodeMember, type CodeType } from '@modelviewer/core';

export interface CodeIndex {
  map: CodeMap;
  members: Map<string, CodeMember>;
  types: Map<string, CodeType>;
  /** File of a member or type id (a type's first part). */
  fileOf: (id: string) => string | null;
  /** Line of a member or type id. */
  lineOf: (id: string) => number | null;
  callsFrom: Map<string, CodeCall[]>;
  callsTo: Map<string, CodeCall[]>;
  /** Members of each type, in declaration order. */
  membersOf: Map<string, CodeMember[]>;
  /** Types that implement each interface or derive from each class. */
  subtypes: Map<string, string[]>;
}

const cache = new WeakMap<CodeMap, CodeIndex>();

export function codeIndex(map: CodeMap): CodeIndex {
  const cached = cache.get(map);
  if (cached) return cached;
  const members = new Map(map.members.map((m) => [m.id, m]));
  const types = new Map(map.types.map((t) => [t.id, t]));
  const group = <T>(list: T[], key: (item: T) => string) => {
    const out = new Map<string, T[]>();
    for (const item of list) {
      const k = key(item);
      const bucket = out.get(k);
      if (bucket) bucket.push(item);
      else out.set(k, [item]);
    }
    return out;
  };
  const subtypes = new Map<string, string[]>();
  for (const t of map.types) {
    for (const sup of [...(t.base ? [t.base] : []), ...t.interfaces]) subtypes.set(sup, [...(subtypes.get(sup) ?? []), t.id]);
  }
  const index: CodeIndex = {
    map,
    members,
    types,
    fileOf: (id) => members.get(id)?.file ?? types.get(id)?.parts[0]?.file ?? null,
    lineOf: (id) => members.get(id)?.line ?? types.get(id)?.parts[0]?.line ?? null,
    callsFrom: group(map.calls, (c) => c.from),
    callsTo: group(map.calls, (c) => c.to),
    membersOf: group(
      [...map.members].sort((a, b) => (a.file === b.file ? a.line - b.line : a.file < b.file ? -1 : 1)),
      (m) => m.type,
    ),
    subtypes,
  };
  cache.set(map, index);
  return index;
}

/** How a member reads in lists: `OrderService.PlaceAsync(Customer, …)`. */
export function memberLabel(member: CodeMember, index?: CodeIndex, withType = true): string {
  if (member.name === TOP_LEVEL_MAIN) return 'top-level statements';
  const type = withType ? index?.types.get(member.type)?.name : undefined;
  return type && member.kind !== 'constructor' ? `${type}.${member.signature}` : member.signature;
}

/** What a member or type id names, for lines and lists. */
export function idLabel(id: string, index: CodeIndex): string {
  const m = index.members.get(id);
  if (m) return memberLabel(m, index);
  return index.types.get(id)?.name ?? id;
}

/** The members of implementing or deriving types that match an interface or virtual member by name and parameters. */
export function implementationsOf(member: CodeMember, index: CodeIndex): CodeMember[] {
  const out: CodeMember[] = [];
  const seen = new Set<string>();
  const queue = [...(index.subtypes.get(member.type) ?? [])];
  while (queue.length) {
    const t = queue.shift()!;
    if (seen.has(t)) continue;
    seen.add(t);
    for (const m of index.membersOf.get(t) ?? []) if (m.kind === member.kind && m.signature === member.signature && m.body) out.push(m);
    queue.push(...(index.subtypes.get(t) ?? []));
  }
  return out;
}
